import type { ShopProductType } from '@kanbu/schema';
import {
  ProductAvailabilityStatus,
  ShopProductStatus,
} from '@kanbu/schema/enums';
import { formatUtils } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge, Button, Checkbox } from '@utima/ui';
import {
  Check,
  DatabaseBackup,
  Eye,
  ExternalLink,
  ImageOff,
  X,
} from 'lucide-react';
import { useMemo } from 'react';

import { ActionsCellWrapper } from '@/components/dataTable/cells/ActionsCellWrapper';
import { SimpleTooltip } from '@/components/index';
import { useEnumTranslations } from '@/hooks';

import { useShopProductMutation } from './useShopProductMutation';

const columnHelper = createColumnHelper<ShopProductType>();

const selectCheckboxClassName =
  'border-muted-fg/60 bg-background hover:border-muted-fg/80 data-[state=unchecked]:bg-background';

const availabilityVariant: Record<
  ProductAvailabilityStatus,
  'success' | 'warning' | 'info' | 'danger'
> = {
  [ProductAvailabilityStatus.InStock]: 'success',
  [ProductAvailabilityStatus.OnOrder]: 'info',
  [ProductAvailabilityStatus.PreOrder]: 'info',
  [ProductAvailabilityStatus.OutOfStock]: 'warning',
  [ProductAvailabilityStatus.Unknown]: 'danger',
};

const statusVariant: Record<ShopProductStatus, 'success' | 'info' | 'danger'> =
  {
    [ShopProductStatus.Pending]: 'info',
    [ShopProductStatus.Embedding]: 'info',
    [ShopProductStatus.Done]: 'success',
    [ShopProductStatus.Failed]: 'danger',
  };

export type UseShopProductsColsOptions = {
  onOpenDetail: (id: string) => void;
};

export function useShopProductsCols({
  onOpenDetail,
}: UseShopProductsColsOptions) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const {
    update: { mutateAsync: update },
    reEmbed: { mutateAsync: reEmbed },
  } = useShopProductMutation();

  return useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => {
          const { selectedIds, onToggleAll } = table.options.meta ?? {};
          const rows = table.getRowModel().rows;
          const ids = rows.map(row => row.original.id);
          const selectedCount = selectedIds
            ? ids.filter(id => selectedIds.has(id)).length
            : 0;
          const allSelected = ids.length > 0 && selectedCount === ids.length;
          const someSelected = selectedCount > 0 && !allSelected;

          return (
            <div data-table='prevent'>
              <Checkbox
                size='sm'
                aria-label={t`Select all rows`}
                checked={
                  allSelected ? true : someSelected ? 'indeterminate' : false
                }
                disabled={ids.length === 0}
                onCheckedChange={checked => onToggleAll?.(ids, !!checked)}
                className={selectCheckboxClassName}
              />
            </div>
          );
        },
        cell: ({ row, table }) => {
          const { selectedIds, onToggleOne } = table.options.meta ?? {};

          return (
            <div data-table='prevent'>
              <Checkbox
                size='sm'
                aria-label={t`Select row`}
                checked={selectedIds?.has(row.original.id) ?? false}
                onCheckedChange={checked =>
                  onToggleOne?.(row.original.id, !!checked)
                }
                className={selectCheckboxClassName}
              />
            </div>
          );
        },
        meta: {
          align: 'center',
        },
      }),
      columnHelper.accessor(row => row.imageUrl, {
        id: 'imageUrl',
        header: t`Image`,
        cell: ({ row }) =>
          row.original.imageUrl ? (
            <img
              src={row.original.imageUrl}
              alt={row.original.name}
              className='size-12 rounded-md border border-border object-cover'
            />
          ) : (
            <div className='flex size-12 items-center justify-center rounded-md border border-border bg-muted/30 text-muted-fg'>
              <ImageOff className='size-5' />
            </div>
          ),
        meta: {
          align: 'center',
        },
      }),
      columnHelper.accessor(row => row.name, {
        id: 'name',
        header: t`Name`,
        cell: ({ row }) => {
          const hasTitleSubtitle =
            row.original.title && row.original.title !== row.original.name;

          return (
            <div className='flex min-w-56 max-w-72 flex-col gap-0.5'>
              <p className='break-all font-medium leading-tight'>
                {row.original.name}
              </p>
              {hasTitleSubtitle ? (
                <p className='text-xs text-gray-600'>{row.original.title}</p>
              ) : (
                row.original.description && (
                  <p className='line-clamp-2 text-xs text-gray-600'>
                    {row.original.description}
                  </p>
                )
              )}
            </div>
          );
        },
        meta: {
          sortable: 'name',
        },
      }),
      columnHelper.accessor(row => row.price, {
        id: 'price',
        header: t`Price`,
        cell: ({ row }) =>
          row.original.price == null
            ? t`Not available`
            : formatUtils.currency(
                Number(row.original.price),
                row.original.currency,
              ),
        meta: {
          sortable: 'price',
          align: 'right',
        },
      }),
      columnHelper.accessor(row => row.availability, {
        id: 'availability',
        header: t`Availability`,
        cell: ({ row }) => (
          <div className='flex flex-col items-start gap-1'>
            <Badge
              size='sm'
              variant={availabilityVariant[row.original.availability]}
              outline
            >
              {
                enumTranslations.productAvailabilityStatus[
                  row.original.availability
                ]
              }
            </Badge>
            <SimpleTooltip
              title={row.original.active ? t`Deactivate` : t`Activate`}
            >
              <button
                type='button'
                data-cta='admin-product-toggle-active-inline'
                data-table='prevent'
                className={
                  row.original.active
                    ? 'inline-flex items-center gap-1 text-xs text-success'
                    : 'inline-flex items-center gap-1 text-xs text-danger'
                }
                onClick={() =>
                  update({ id: row.original.id, active: !row.original.active })
                }
              >
                {row.original.active ? (
                  <Check className='size-3.5' />
                ) : (
                  <X className='size-3.5' />
                )}
                {row.original.active ? t`Active` : t`Inactive`}
              </button>
            </SimpleTooltip>
          </div>
        ),
        meta: {
          sortable: 'availability',
        },
      }),
      columnHelper.accessor(row => row.status, {
        id: 'status',
        header: t`Status`,
        cell: ({ row }) => (
          <Badge size='sm' variant={statusVariant[row.original.status]} outline>
            {enumTranslations.shopProductStatus[row.original.status]}
          </Badge>
        ),
        meta: {
          sortable: 'status',
        },
      }),
      columnHelper.accessor(row => row.brand, {
        id: 'brand',
        header: t`Brand / Category`,
        cell: ({ row }) => {
          const categories = row.original.categories ?? [];
          const visibleCategories = categories.slice(0, 2);
          const hiddenCategories = categories.slice(2);

          return (
            <div className='flex flex-col gap-1 text-xs'>
              {row.original.brand && (
                <span className='font-medium text-gray-800'>
                  {row.original.brand}
                </span>
              )}
              {categories.length > 0 && (
                <div className='flex flex-wrap items-center gap-1'>
                  {visibleCategories.map(category => (
                    <Badge
                      key={category.id}
                      size='sm'
                      variant='muted'
                      outline
                      className='text-gray-800'
                    >
                      {category.displayName ?? category.name}
                    </Badge>
                  ))}
                  {hiddenCategories.length > 0 && (
                    <SimpleTooltip
                      title={hiddenCategories
                        .map(category => category.displayName ?? category.name)
                        .join(', ')}
                    >
                      <Badge
                        size='sm'
                        variant='muted'
                        outline
                        className='text-gray-800'
                      >
                        {t`+${hiddenCategories.length} others`}
                      </Badge>
                    </SimpleTooltip>
                  )}
                </div>
              )}
            </div>
          );
        },
        meta: {
          sortable: 'brand',
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: t`Actions`,
        cell: ({ row }) => {
          const product = row.original;

          return (
            <ActionsCellWrapper>
              <SimpleTooltip title={t`Open source URL`}>
                <Button
                  data-cta='admin-product-open-source-button'
                  variant='ghost'
                  outline
                  size='icon-xs'
                  onClick={() =>
                    window.open(product.url, '_blank', 'noopener,noreferrer')
                  }
                >
                  <ExternalLink className='size-4' />
                </Button>
              </SimpleTooltip>
              <SimpleTooltip title={t`View details`}>
                <Button
                  data-cta='admin-product-view-details-button'
                  variant='ghost'
                  outline
                  size='icon-xs'
                  onClick={() => onOpenDetail(product.id)}
                >
                  <Eye className='size-4' />
                </Button>
              </SimpleTooltip>
              <SimpleTooltip title={t`Re-embed`}>
                <Button
                  data-cta='admin-product-reembed-button'
                  variant='ghost'
                  className='text-info'
                  outline
                  size='icon-xs'
                  onClick={() => reEmbed({ id: product.id })}
                >
                  <DatabaseBackup className='size-4' />
                </Button>
              </SimpleTooltip>
            </ActionsCellWrapper>
          );
        },
        meta: {
          align: 'center',
        },
      }),
    ],
    [
      t,
      enumTranslations.productAvailabilityStatus,
      enumTranslations.shopProductStatus,
      onOpenDetail,
      update,
      reEmbed,
    ],
  );
}
